import { Directive, inject } from '@angular/core';
import { untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { Observable, combineLatest } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';

import { getPermissionWarningMessage } from '@app/utils/permission-message';
import { PuSubscribable } from '@app/utils/pu-subscribe';
import { selectUserGeneralRole, selectUserPermissionLevel } from '@main-application/store/selectors/user.selectors';
import { PermissionLevelType } from '@shared/enums/permission-level.enum';
import { checkPermissionLevel } from '@shared/functions/check-permission-level.function';
import { SnackbarErrorMessage } from '@ui-components/components/customized-snackbar/snackbar-message.enum';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';

@Directive()
export abstract class TemplateEditorBase extends PuSubscribable {
  protected store: Store<{}> = inject(Store);
  protected snackbarService: SnackbarService = inject(SnackbarService);

  hasEditRight = false;
  hasEditRight$: Observable<boolean>;

  constructor(..._args: unknown[]) {
    super();
    this.hasEditRight$ = this.store.select(selectUserPermissionLevel).pipe(
      untilDestroyed(this),
      filter((permissionLevel: PermissionLevelType[]) => !!permissionLevel?.length),
      map(permissionLevels => checkPermissionLevel(permissionLevels, PermissionLevelType.InspectionTemplates_Edit)),
      tap(hasRight => (this.hasEditRight = hasRight))
    );
    this.hasEditRight$.subscribe().untilDestroyed(this);
  }

  checkEditRight(isEditable: boolean, notify = true): boolean {
    if (!isEditable) {
      this.snackbarService.warning(SnackbarErrorMessage.CannotEditGlobalTemplate, 5000);
    }
    if (!this.hasEditRight && notify) {
      combineLatest([this.store.select(selectUserGeneralRole).pipe(filter(Boolean), take(1))])
        .pipe(take(1))
        .subscribe(([userRole]) => {
          this.snackbarService.warning(
            getPermissionWarningMessage(PermissionLevelType.InspectionTemplates_Edit, 'your account manager', userRole)
          );
        })
        .untilDestroyed(this);
    }
    return isEditable && this.hasEditRight;
  }
}
